import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowTrendDown,
  faArrowTrendUp,
  faCircle,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/Navbar';
import { axiosInstance } from '../../context/AuthContext';
import {
  generateDemoStockSeries,
  tickerFromArtwork,
} from '../../utils/demoStockData';

const isMongoObjectId = (value) => /^[a-f\d]{24}$/i.test(value);

const RANGES = ['1D', '1W', '1M'];

const ArtworkStock = () => {
  const { artworkId } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('1D');
  const [tick, setTick] = useState(0);
  const [livePulse, setLivePulse] = useState(true);

  useEffect(() => {
    if (!artworkId) {
      setLoading(false);
      return;
    }

    const artworkUrl = isMongoObjectId(artworkId)
      ? `/artworks/${artworkId}`
      : `/artworks/slug/${artworkId}`;

    axiosInstance
      .get(artworkUrl)
      .then((res) => setArtwork(res.data))
      .catch(() => setArtwork(null))
      .finally(() => setLoading(false));
  }, [artworkId]);

  const seed = useMemo(() => {
    const key = artwork?._id || artworkId || 'demo';
    return key.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  }, [artwork?._id, artworkId]);

  const series = useMemo(
    () => generateDemoStockSeries(seed + tick, range),
    [seed, tick, range]
  );

  const ticker = tickerFromArtwork(artwork, artworkId);
  const isUp = series.quote.change >= 0;
  const backPath = `/artozphere/artwork/${artwork?.slug || artworkId}`;

  useEffect(() => {
    if (!livePulse) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 4000);
    return () => clearInterval(id);
  }, [livePulse, range]);

  const chartOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      animationDuration: 600,
      grid: { left: 48, right: 24, top: 24, bottom: 36 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#e2e8f0', fontSize: 12 },
        axisPointer: { type: 'cross', lineStyle: { color: '#475569' } },
      },
      xAxis: {
        type: 'category',
        data: series.labels,
        axisLine: { lineStyle: { color: '#334155' } },
        axisLabel: { color: '#94a3b8', fontSize: 10 },
        splitLine: { show: false },
      },
      yAxis: {
        scale: true,
        axisLine: { show: false },
        axisLabel: {
          color: '#94a3b8',
          fontSize: 10,
          formatter: (v) => `$${v}`,
        },
        splitLine: { lineStyle: { color: '#1e293b' } },
      },
      series: [
        {
          name: 'Price',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: series.closes,
          lineStyle: { width: 2, color: isUp ? '#34d399' : '#f87171' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: isUp
                    ? 'rgba(52, 211, 153, 0.35)'
                    : 'rgba(248, 113, 113, 0.35)',
                },
                { offset: 1, color: 'rgba(15, 23, 42, 0)' },
              ],
            },
          },
        },
      ],
    }),
    [series, isUp]
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 pt-8 pb-16 ml-0 md:ml-20">
        <Link
          to={backPath}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition mb-6"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-3 h-3" />
          Back to artwork
        </Link>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-10 w-10 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faChartLine} className="text-emerald-400" />
                  <span className="font-mono text-emerald-400 text-lg font-semibold">
                    {ticker}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    Demo
                  </span>
                  {livePulse && (
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <FontAwesomeIcon icon={faCircle} className="w-2 h-2 animate-pulse" />
                      Simulated live
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-serif text-white">
                  {artwork?.title || 'Artwork stock'}
                </h1>
                <p className="text-slate-400 text-sm mt-1">
                  Fractional art market · placeholder feed
                </p>
              </div>

              <div className="text-right">
                <p className="text-4xl font-semibold tabular-nums">${series.quote.price}</p>
                <p
                  className={`flex items-center justify-end gap-1 text-sm font-medium ${
                    isUp ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  <FontAwesomeIcon icon={isUp ? faArrowTrendUp : faArrowTrendDown} />
                  {isUp ? '+' : ''}
                  {series.quote.change} ({isUp ? '+' : ''}
                  {series.quote.changePct}%)
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                    range === r
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {r}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setLivePulse((p) => !p)}
                className="ml-auto px-4 py-1.5 rounded-lg text-sm bg-slate-800 text-slate-300 hover:bg-slate-700"
              >
                {livePulse ? 'Pause updates' : 'Resume updates'}
              </button>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl shadow-black/40">
              <ReactECharts
                option={chartOption}
                style={{ height: 380, width: '100%' }}
                notMerge
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Day high', value: `$${series.quote.high}` },
                { label: 'Day low', value: `$${series.quote.low}` },
                { label: 'Volume', value: series.quote.volume.toLocaleString() },
                { label: 'Market', value: 'ARTX Demo' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3"
                >
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{stat.label}</p>
                  <p className="text-lg font-medium text-slate-100 mt-0.5">{stat.value}</p>
                </div>
              ))}
            </div>

            <p className="mt-6 text-center text-xs text-slate-500 max-w-xl mx-auto">
              Prices are simulated for preview. This page is wired for a future live market data
              integration.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ArtworkStock;
